import React from "react";
import { Link } from "react-router-dom";
import PulseBeams from "../components/ui/pulse-beams";
import { Users, Monitor, Package, Calendar, Radio } from "lucide-react";

// Luxury Radial Pattern (Broadcast Hub Design) - 600x800
// Center Hub: (300, 400) - Radius: 150px
const beams = [
  {
    // Top Spoke (Monitor -> Center)
    path: "M300 250 L300 400",
    gradientConfig: {
      initial: { x1: "0%", x2: "0%", y1: "0%", y2: "0%" },
      animate: { x1: ["0%", "0%"], x2: ["0%", "0%"], y1: ["0%", "100%"], y2: ["10%", "90%"] },
      transition: { duration: 6, repeat: Infinity, ease: [0.4, 0, 0.2, 1], repeatDelay: 0 }
    },
    connectionPoints: [{ cx: 300, cy: 250, r: 3 }]
  },
  {
    // Right Spoke (Calendar -> Center)
    path: "M450 400 L300 400",
    gradientConfig: {
      initial: { x1: "0%", x2: "0%", y1: "0%", y2: "0%" },
      animate: { x1: ["0%", "100%"], x2: ["10%", "90%"], y1: ["0%", "0%"], y2: ["0%", "0%"] },
      transition: { duration: 6, repeat: Infinity, ease: [0.4, 0, 0.2, 1], repeatDelay: 2 }
    },
    connectionPoints: [{ cx: 450, cy: 400, r: 3 }]
  },
  {
    // Bottom Spoke (Package -> Center)
    path: "M300 550 L300 400",
    gradientConfig: {
      initial: { x1: "0%", x2: "0%", y1: "0%", y2: "0%" },
      animate: { x1: ["0%", "0%"], x2: ["0%", "0%"], y1: ["0%", "100%"], y2: ["10%", "90%"] },
      transition: { duration: 6, repeat: Infinity, ease: [0.4, 0, 0.2, 1], repeatDelay: 4 }
    },
    connectionPoints: [{ cx: 300, cy: 550, r: 3 }]
  },
  {
    // Left Spoke (Users -> Center)
    path: "M150 400 L300 400",
    gradientConfig: {
      initial: { x1: "0%", x2: "0%", y1: "0%", y2: "0%" },
      animate: { x1: ["0%", "100%"], x2: ["10%", "90%"], y1: ["0%", "0%"], y2: ["0%", "0%"] },
      transition: { duration: 6, repeat: Infinity, ease: [0.4, 0, 0.2, 1], repeatDelay: 6 }
    },
    connectionPoints: [{ cx: 150, cy: 400, r: 3 }]
  },
  {
    // Circular Orbit (Elegant ring connecting outer icons)
    path: "M450 400 A150 150 0 1 1 449.9 400",
    gradientConfig: {
      initial: { x1: "0%", x2: "0%", y1: "0%", y2: "0%" },
      animate: { x1: ["0%", "100%"], x2: ["0%", "95%"], y1: ["0%", "0%"], y2: ["0%", "0%"] },
      transition: { duration: 8, repeat: Infinity, ease: "linear", repeatDelay: 0 }
    },
    connectionPoints: [{ cx: 300, cy: 400, r: 6 }] // Center hub dot (larger)
  }
];

// Luxury Radial Coordinates (600x800 viewport, 150px radius)
// Top: 300, 250 -> 50%, 31.25%
// Right: 450, 400 -> 75%, 50%
// Bottom: 300, 550 -> 50%, 68.75%
// Left: 150, 400 -> 25%, 50%
// Center Hub: 300, 400 -> 50%, 50%

const authIcons = [
  { icon: Monitor, x: "50%", y: "31.25%", delay: 0 },       // Live Production (Top)
  { icon: Calendar, x: "75%", y: "50%", delay: 2 },         // Project Scheduling (Right)
  { icon: Package, x: "50%", y: "68.75%", delay: 4 },       // Equipment Management (Bottom)
  { icon: Users, x: "25%", y: "50%", delay: 6 },            // Crew Management (Left)
  { icon: Radio, x: "50%", y: "50%", delay: 1.5 },          // Center Hub - Live Operations
];

const AuthLayout = ({ children, title, subtitle }) => {
  return (
    <div className="min-h-screen w-full flex bg-background">
      {/* Left Side - Brand/Animation Section */}
      <div className="hidden lg:flex lg:w-1/2 relative bg-[#1D1D1B] items-center justify-center overflow-hidden">

        {/* 3D Animated Network Background - Centered & Aspect Constrained */}
        <div className="w-full max-w-[600px] aspect-[3/4] relative z-0 opacity-50">
           <PulseBeams
             beams={beams}
             width={600}
             height={800}
             baseColor="#57534D"
             accentColor="#ECE1CE"
             gradientColors={{
               start: "#ECE1CE",
               middle: "#E8D1AB",
               end: "#1D1D1B"
             }}
             icons={authIcons}
             className="w-full h-full"
           />
        </div>

        {/* Subtle Luxury Glows */}
        <div className="absolute top-[-20%] left-[-20%] w-[70%] h-[70%] rounded-full bg-[#ECE1CE] opacity-3 blur-[140px] pointer-events-none" />
        <div className="absolute bottom-[-10%] right-[-10%] w-[60%] h-[60%] rounded-full bg-[#E8D1AB] opacity-5 blur-[120px] pointer-events-none" />
        <div className="absolute top-[50%] left-[50%] transform -translate-x-1/2 -translate-y-1/2 w-[40%] h-[40%] rounded-full bg-[#E8D1AB] opacity-4 blur-[100px] pointer-events-none" />

        {/* Content Overlay */}
        <div className="absolute inset-0 z-10 flex flex-col justify-between w-full p-12 text-[#ECE1CE] pointer-events-none">
          <div className="pointer-events-auto">
            <Link to="/" className="inline-block">
              <img
                src="/images/logos/BEIGELOGO.PNG"
                alt="Beige Logo"
                className="h-12 invert brightness-0 filter"
                style={{ filter: "brightness(0) invert(1)" }}
              />
            </Link>
          </div>

          <div className="space-y-6">
            <h1 className="text-4xl font-medium tracking-tight leading-tight">
              The world's most seamless <br/>
              <span className="text-[#E8D1AB]">production platform.</span>
            </h1>
            <p className="text-lg text-white/60 max-w-md">
              Connect your crew, equipment, and vision in one seamless dashboard.
            </p>
          </div>

          <div className="flex items-center gap-4 text-sm text-white/40">
            <span>© 2026 Beige Inc.</span>
          </div>
        </div>
      </div>

      {/* Right Side - Form Section */}
      <div className="flex-1 flex items-center justify-center bg-background p-8 relative">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-stone-50 via-background to-background -z-10" />

        <div className="w-full max-w-md space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-700">
            <div className="text-center lg:text-left">
                <h2 className="text-2xl font-semibold tracking-tight">{title}</h2>
                {subtitle && <p className="text-muted-foreground mt-2">{subtitle}</p>}
            </div>
            {children}
        </div>
      </div>
    </div>
  );
};

export default AuthLayout;
